// Agency Leaderboard — Rankings / TV Mode / AEP Season / Contests
// Full overhaul: 4-tab design with dark theme (bg-gray-950)
import React, {
  useState, useEffect, useCallback, useRef
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy, ArrowLeft, RefreshCw, TrendingUp,
  Users, CheckCircle, Crown, Medal, Star, Tv,
  Calendar, Award, Plus, X, ChevronUp, ChevronDown, Minus,
  Maximize, Minimize, BarChart2, Target, Clock,
  ChevronRight, ChevronLeft, Gift
} from 'lucide-react';
import {
  LineChart, Line, XAxis,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { TourBubble } from '../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ============================================================================
// CONSTANTS
// ============================================================================
const TABS = [
  { id: 'rankings', label: 'Rankings', icon: Trophy },
  { id: 'tv', label: 'TV Mode', icon: Tv },
  { id: 'aep', label: 'AEP Season', icon: Star },
  { id: 'contests', label: 'Contests', icon: Award },
];

const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'aep', label: 'AEP 2026' },
  { value: 'custom', label: 'Custom' },
];

const METRICS = [
  { value: 'enrollments', label: 'Enrollments', icon: Users },
  { value: 'retention_rate', label: 'Retention Rate', icon: TrendingUp },
  { value: 'client_satisfaction', label: 'Client Satisfaction', icon: Star },
  { value: 'response_time', label: 'Response Time', icon: Clock },
  { value: 'tasks_completed', label: 'Tasks Completed', icon: CheckCircle },
  { value: 'commission', label: 'Commission', icon: Trophy },
];

const TV_PANELS = ['top5', 'activity', 'goal', 'rising'];
const TV_ROTATE_OPTIONS = [15, 30, 60]; // seconds

const TOUR_STEPS = [
  { type: 'splash', title: 'Agency Leaderboard', description: 'Your command center for team performance. Rankings, TV Mode for the office screen, AEP Season competition, and Contests — all in one place.' },
  { target: 'lb-tabs', emoji: '🗂️', title: 'Four Views', description: 'Switch between Rankings, TV Mode (for your office screen), AEP Season competition, and Contests.', position: 'bottom' },
  { target: 'lb-period', emoji: '📅', title: 'Time Period', description: 'Filter by Today, Week, Month, Quarter, AEP season, or a custom date range.', position: 'bottom' },
  { target: 'lb-metrics', emoji: '📊', title: 'Rank By Metric', description: 'Change the primary ranking metric — enrollments, retention rate, satisfaction, response time, tasks, or commission.', position: 'bottom' },
  { target: 'lb-table', emoji: '🏆', title: 'Rankings Table', description: 'Agents ranked with trend sparklines, rank-change indicators, and performance badges. Click any row for a full breakdown.', position: 'top' },
  { type: 'celebration', title: 'Motivate Your Team!', description: 'Leaderboards drive healthy competition. Share TV Mode in team meetings and celebrate wins publicly.' },
];

// ============================================================================
// HELPER COMPONENTS
// ============================================================================
function RankBadge({ rank }) {
  if (rank === 1) return <span title="1st Place"><Crown size={20} className="text-amber-400" /></span>;
  if (rank === 2) return <span title="2nd Place"><Medal size={20} className="text-gray-400" /></span>;
  if (rank === 3) return <span title="3rd Place"><Medal size={20} className="text-amber-700" /></span>;
  return (
    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-800 text-gray-400 text-xs font-bold tabular-nums">
      {rank}
    </span>
  );
}

function RankChange({ change }) {
  if (!change || change === 0) return <Minus size={12} className="text-gray-600" />;
  if (change > 0) return (
    <span className="flex items-center gap-0.5 text-emerald-400 text-xs font-medium tabular-nums">
      <ChevronUp size={13} />{change}
    </span>
  );
  return (
    <span className="flex items-center gap-0.5 text-red-400 text-xs font-medium tabular-nums">
      <ChevronDown size={13} />{Math.abs(change)}
    </span>
  );
}

function BadgePill({ badge }) {
  if (!badge) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-gray-300 border border-gray-700">
      {badge.emoji} {badge.label}
    </span>
  );
}

function MiniSparkline({ data, color = '#f59e0b' }) {
  if (!data || data.length < 2) {
    return <div className="w-16 h-6 flex items-center justify-center text-gray-700 text-xs">—</div>;
  }
  const values = data.map(d => d.value || 0);
  return (
    <ResponsiveContainer width={64} height={24}>
      <LineChart data={data}>
        <Line
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={1.5}
          dot={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function Spinner({ size = 24 }) {
  return <RefreshCw size={size} className="text-amber-400 animate-spin" />;
}

function MetricValue({ metric, value }) {
  if (value === null || value === undefined) return <span className="text-gray-600">—</span>;
  switch (metric) {
    case 'commission': return <span>${(value).toLocaleString()}</span>;
    case 'response_time': return <span>{value}h</span>;
    case 'client_satisfaction': return <span>{Number(value).toFixed(1)}/5</span>;
    case 'retention_rate': return <span>{value}%</span>;
    default: return <span>{Number(value).toLocaleString()}</span>;
  }
}

// ============================================================================
// AGENT BREAKDOWN SLIDE-OUT
// ============================================================================
function AgentBreakoutPanel({ agentId, agentName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agentId) return;
    setLoading(true);
    fetch(`${BASE}/agency-leaderboard/agent/${agentId}/breakdown`, { credentials: 'include' })
      .then(r => r.json())
      .then(json => { if (json.ok) setData(json); })
      .catch(err => console.error('[agent breakdown]', err))
      .finally(() => setLoading(false));
  }, [agentId]);

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-gray-900 border-l border-gray-800 z-50 flex flex-col shadow-2xl">
      <div className="flex items-center justify-between p-4 border-b border-gray-800">
        <h3 className="text-white font-semibold truncate">{agentName} — Breakdown</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-800 text-gray-400">
          <X size={18} />
        </button>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Spinner />
        </div>
      ) : !data ? (
        <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">No data available</div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Key metrics */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Enrollments', value: data.metrics?.enrollments, color: 'text-amber-400' },
              { label: 'Tasks Done', value: `${data.metrics?.tasksCompleted}/${data.metrics?.totalTasks}`, color: 'text-emerald-400' },
              { label: 'Comms Out', value: data.metrics?.commsOut, color: 'text-blue-400' },
              { label: 'Retention', value: `${data.metrics?.retentionRate}%`, color: 'text-purple-400' },
            ].map(m => (
              <div key={m.label} className="bg-gray-800 rounded-lg p-3">
                <p className="text-gray-400 text-xs mb-1">{m.label}</p>
                <p className={`text-xl font-bold tabular-nums ${m.color}`}>{m.value ?? '—'}</p>
              </div>
            ))}
          </div>

          {/* Comparison bar */}
          {data.teamAvg?.enrollments > 0 && (
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-2">Enrollments vs. Team Avg</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-400 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (data.metrics?.enrollments / (data.teamAvg.enrollments * 2 || 1)) * 100)}%` }}
                  />
                </div>
                <span className="text-xs text-gray-400 tabular-nums w-16 text-right">
                  Avg: {Number(data.teamAvg.enrollments).toFixed(1)}
                </span>
              </div>
            </div>
          )}

          {/* Sparklines from snapshots */}
          {data.sparklines?.length > 1 && (
            <div className="bg-gray-800 rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-2">30-Day Enrollment Trend</p>
              <ResponsiveContainer width="100%" height={80}>
                <LineChart data={data.sparklines}>
                  <Line type="monotone" dataKey="enrollments" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  <XAxis dataKey="snapshot_date" hide />
                  <Tooltip
                    contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 6 }}
                    labelStyle={{ color: '#9ca3af', fontSize: 11 }}
                    itemStyle={{ color: '#f59e0b', fontSize: 11 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// RANKINGS TAB
// ============================================================================
function RankingsTab({ userRole }) {
  const [period, setPeriod] = useState('month');
  const [metric, setMetric] = useState('enrollments');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period, metric });
      if (period === 'custom' && customStart && customEnd) {
        params.set('customStart', customStart);
        params.set('customEnd', customEnd);
      }
      const [rankRes, sumRes] = await Promise.all([
        fetch(`${BASE}/agency-leaderboard/rankings?${params}`, { credentials: 'include' }),
        fetch(`${BASE}/agency-leaderboard/team-summary?period=${period}&${period === 'custom' ? `customStart=${customStart}&customEnd=${customEnd}` : ''}`, { credentials: 'include' }),
      ]);
      const rankJson = await rankRes.json();
      const sumJson = await sumRes.json();
      if (rankJson.ok) setData(rankJson);
      if (sumJson.ok) setSummary(sumJson.data);
    } catch (err) {
      console.error('[rankings]', err);
    } finally {
      setLoading(false);
    }
  }, [period, metric, customStart, customEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isManager = ['agency_owner', 'agency_admin', 'admin', 'fmo', 'founder'].includes(userRole);

  return (
    <div className="space-y-5">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Period selector */}
        <div data-tour="lb-period" className="flex bg-gray-900 border border-gray-800 rounded-lg p-0.5 flex-wrap gap-0.5">
          {PERIODS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                period === p.value
                  ? 'bg-amber-400 text-gray-950 font-semibold'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          onClick={fetchData}
          className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Custom date range */}
      {period === 'custom' && (
        <div className="flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-lg p-3">
          <Calendar size={14} className="text-gray-400" />
          <input
            type="date"
            value={customStart}
            onChange={e => setCustomStart(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={customEnd}
            onChange={e => setCustomEnd(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white"
          />
        </div>
      )}

      {/* Team summary cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Team Size', value: summary.teamSize, sub: `${summary.teamSize} agents`, icon: Users, color: 'text-blue-400' },
            { label: 'Enrollments', value: summary.totalEnrollments, icon: Trophy, color: 'text-amber-400' },
            { label: 'Tasks Done', value: summary.totalTasks, icon: CheckCircle, color: 'text-emerald-400' },
            { label: 'Top Performer', value: summary.topPerformer?.name || '—', sub: summary.topPerformer ? `${summary.topPerformer.enrollments} enrollments` : null, icon: Crown, color: 'text-amber-400', truncate: true },
          ].map(card => (
            <div key={card.label} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-400 text-xs">{card.label}</p>
                <card.icon size={14} className={card.color} />
              </div>
              <p className={`text-xl font-bold ${card.color} tabular-nums ${card.truncate ? 'truncate text-base' : ''}`}>
                {card.value}
              </p>
              {card.sub && !card.truncate && <p className="text-gray-600 text-xs mt-0.5">{card.sub}</p>}
              {card.sub && card.truncate && <p className="text-gray-600 text-xs mt-0.5 tabular-nums">{card.sub}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Metric selector */}
      <div data-tour="lb-metrics" className="flex flex-wrap gap-2">
        {METRICS.map(m => (
          <button
            key={m.value}
            onClick={() => setMetric(m.value)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              metric === m.value
                ? 'bg-amber-400 text-gray-950 border-amber-400'
                : 'bg-gray-900 text-gray-400 border-gray-800 hover:border-gray-600 hover:text-white'
            }`}
          >
            <m.icon size={13} />
            {m.label}
          </button>
        ))}
      </div>

      {/* Rankings table */}
      <div data-tour="lb-table" className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-16 flex flex-col items-center gap-3">
            <Spinner size={32} />
            <p className="text-gray-500 text-sm">Loading rankings...</p>
          </div>
        ) : !data?.data?.length ? (
          <div className="p-16 flex flex-col items-center gap-3 text-gray-600">
            <Trophy size={40} className="opacity-30" />
            <p>No data for this period</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-14">Rank</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {METRICS.find(m => m.value === metric)?.label}
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Tasks</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Comms</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Trend</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">Chg</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden xl:table-cell">Badge</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((agent, i) => {
                  const isTop3 = agent.rank <= 3;
                  const isSelected = selectedAgent?.agentId === agent.agentId;
                  // Hide "needs_support" badge from non-managers in the table
                  const badge = agent.badge && (agent.badge.public || isManager) ? agent.badge : null;

                  return (
                    <tr
                      key={agent.agentId}
                      onClick={() => setSelectedAgent(isSelected ? null : agent)}
                      className={`border-b border-gray-800/50 cursor-pointer transition-colors ${
                        isSelected ? 'bg-amber-400/5 border-amber-400/20' :
                        isTop3 ? 'bg-amber-400/3 hover:bg-gray-800/60' : 'hover:bg-gray-800/40'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <RankBadge rank={agent.rank} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className={`font-semibold text-sm ${isTop3 ? 'text-white' : 'text-gray-200'}`}>
                            {agent.name}
                          </p>
                          <p className="text-gray-600 text-xs truncate max-w-[180px]">{agent.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold tabular-nums ${isTop3 ? 'text-amber-400' : 'text-white'}`}>
                          <MetricValue metric={metric} value={agent.primaryScore} />
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right hidden sm:table-cell text-gray-300 tabular-nums text-sm">
                        {agent.secondaryMetrics?.tasksCompleted ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right hidden md:table-cell text-gray-300 tabular-nums text-sm">
                        {agent.secondaryMetrics?.commsOut ?? '—'}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex justify-center">
                          <MiniSparkline
                            data={agent.sparkline}
                            color={isTop3 ? '#f59e0b' : '#6b7280'}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <RankChange change={agent.rankChange} />
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell">
                        <BadgePill badge={badge} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Badge legend */}
      <div className="flex flex-wrap gap-2 text-xs text-gray-600">
        <span>Badges:</span>
        <BadgePill badge={{ emoji: '🔥', label: 'On Fire' }} />
        <BadgePill badge={{ emoji: '⭐', label: 'Consistent' }} />
        <BadgePill badge={{ emoji: '📈', label: 'Rising Star' }} />
        {isManager && <BadgePill badge={{ emoji: '💙', label: 'Needs Support' }} />}
      </div>

      {/* Agent slide-out */}
      {selectedAgent && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setSelectedAgent(null)} />
          <AgentBreakoutPanel
            agentId={selectedAgent.agentId}
            agentName={selectedAgent.name}
            onClose={() => setSelectedAgent(null)}
          />
        </>
      )}
    </div>
  );
}

// ============================================================================
// TV MODE TAB
// ============================================================================
function TVModeTab() {
  const [tvData, setTvData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [panelIdx, setPanelIdx] = useState(0);
  const [rotateInterval, setRotateInterval] = useState(30);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const containerRef = useRef(null);
  const rotateRef = useRef(null);

  const fetchTV = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/agency-leaderboard/tv`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) {
        setTvData(json.data);
        // Pulse on new activity
        setPulse(true);
        setTimeout(() => setPulse(false), 1500);
      }
    } catch (err) {
      console.error('[tv]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTV(); }, [fetchTV]);

  // Auto-rotate panels
  useEffect(() => {
    clearInterval(rotateRef.current);
    rotateRef.current = setInterval(() => {
      setPanelIdx(i => (i + 1) % TV_PANELS.length);
    }, rotateInterval * 1000);
    return () => clearInterval(rotateRef.current);
  }, [rotateInterval]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.().catch(() => {});
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Refresh every 60s
  useEffect(() => {
    const t = setInterval(fetchTV, 60000);
    return () => clearInterval(t);
  }, [fetchTV]);

  const now = new Date();
  const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const renderPanel = () => {
    if (!tvData) return null;
    const panel = TV_PANELS[panelIdx];

    if (panel === 'top5') return (
      <div className="flex-1 flex flex-col gap-4">
        <h2 className={`font-bold text-amber-400 ${isFullscreen ? 'text-4xl' : 'text-2xl'}`}>
          Top Performers — This Month
        </h2>
        {tvData.top5.length === 0 ? (
          <p className="text-gray-500">No data yet this month</p>
        ) : (
          <div className="flex flex-col gap-3">
            {tvData.top5.map(agent => (
              <div key={agent.id}
                className={`flex items-center gap-4 bg-gray-900 border border-gray-800 rounded-xl ${isFullscreen ? 'p-6' : 'p-4'}`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${
                  agent.rank === 1 ? 'bg-amber-400/20' : agent.rank === 2 ? 'bg-gray-500/20' : 'bg-amber-800/20'
                }`}>
                  <RankBadge rank={agent.rank} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-bold text-white truncate ${isFullscreen ? 'text-3xl' : 'text-lg'}`}>
                    {agent.name}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`font-bold tabular-nums ${isFullscreen ? 'text-4xl' : 'text-2xl'} ${agent.rank === 1 ? 'text-amber-400' : 'text-white'}`}>
                    {agent.enrollments}
                  </p>
                  <p className="text-gray-500 text-xs">enrollments</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    if (panel === 'activity') return (
      <div className="flex-1 flex flex-col gap-4">
        <h2 className={`font-bold text-amber-400 ${isFullscreen ? 'text-4xl' : 'text-2xl'}`}>
          Live Activity Feed
        </h2>
        <div className="flex flex-col gap-2 overflow-hidden">
          {tvData.liveActivity.length === 0 ? (
            <p className="text-gray-500">No recent activity in the last 24 hours</p>
          ) : (
            tvData.liveActivity.map((act, i) => (
              <div key={i}
                className={`flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-lg ${isFullscreen ? 'p-5' : 'p-3'} ${i === 0 && pulse ? 'border-amber-400/40 bg-amber-400/5' : ''}`}
              >
                <div className={`w-8 h-8 rounded-full bg-amber-400/20 flex items-center justify-center flex-shrink-0 ${i === 0 && pulse ? 'animate-pulse' : ''}`}>
                  <Trophy size={14} className="text-amber-400" />
                </div>
                <p className={`text-gray-200 ${isFullscreen ? 'text-xl' : 'text-sm'}`}>
                  <span className="text-white font-semibold">{act.agentName}</span> just enrolled a client
                  {act.carrier ? ` — ${act.carrier}` : ''}
                  {act.planType ? ` ${act.planType}` : ''} 🎉
                </p>
                <span className="text-gray-600 text-xs ml-auto tabular-nums flex-shrink-0">
                  {act.at ? new Date(act.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    );

    if (panel === 'goal') return (
      <div className="flex-1 flex flex-col gap-6">
        <h2 className={`font-bold text-amber-400 ${isFullscreen ? 'text-4xl' : 'text-2xl'}`}>
          Monthly Goal Progress
        </h2>
        {tvData.teamGoal ? (
          <>
            <div className={`flex justify-between ${isFullscreen ? 'text-5xl' : 'text-3xl'} font-bold tabular-nums`}>
              <span className="text-white">{tvData.teamGoal.current}</span>
              <span className="text-gray-600">/ {tvData.teamGoal.goal}</span>
            </div>
            <div className="h-8 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-400 rounded-full transition-all duration-1000"
                style={{ width: `${tvData.teamGoal.pct}%` }}
              />
            </div>
            <p className={`text-gray-400 ${isFullscreen ? 'text-2xl' : 'text-base'}`}>
              {tvData.teamGoal.pct}% of monthly target
            </p>
          </>
        ) : (
          <p className="text-gray-500">No goal data available</p>
        )}
      </div>
    );

    if (panel === 'rising') return (
      <div className="flex-1 flex flex-col gap-6 items-center justify-center text-center">
        <div className={`${isFullscreen ? 'text-8xl' : 'text-5xl'}`}>📈</div>
        <h2 className={`font-bold text-amber-400 ${isFullscreen ? 'text-4xl' : 'text-2xl'}`}>
          Rising Star Today
        </h2>
        {tvData.risingStars?.length > 0 ? (
          <>
            <p className={`text-white font-bold ${isFullscreen ? 'text-6xl' : 'text-3xl'}`}>
              {tvData.risingStars[0].name}
            </p>
            <p className={`text-amber-400 tabular-nums ${isFullscreen ? 'text-4xl' : 'text-xl'}`}>
              {tvData.risingStars[0].today_enrollments} enrollments today
            </p>
          </>
        ) : (
          <p className="text-gray-500">No enrollments yet today</p>
        )}
      </div>
    );

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Controls bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2">
          <Clock size={14} className="text-gray-500" />
          <span className="text-xs text-gray-400">Rotate every:</span>
          {TV_ROTATE_OPTIONS.map(s => (
            <button key={s}
              onClick={() => setRotateInterval(s)}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                rotateInterval === s ? 'bg-amber-400 text-gray-950' : 'text-gray-400 hover:text-white'
              }`}
            >
              {s}s
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {TV_PANELS.map((_, i) => (
            <button
              key={i}
              onClick={() => setPanelIdx(i)}
              className={`w-2 h-2 rounded-full transition-colors ${i === panelIdx ? 'bg-amber-400' : 'bg-gray-700 hover:bg-gray-600'}`}
            />
          ))}
        </div>
        <button
          onClick={fetchTV}
          className="p-2 rounded-lg hover:bg-gray-800 text-gray-400"
          title="Refresh"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
        <button
          onClick={toggleFullscreen}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-400 text-gray-950 text-sm font-semibold hover:bg-amber-300 transition-colors"
        >
          {isFullscreen ? <Minimize size={14} /> : <Maximize size={14} />}
          {isFullscreen ? 'Exit TV Mode' : 'Enter TV Mode'}
        </button>
      </div>

      {/* TV panel */}
      <div
        ref={containerRef}
        className={`bg-gray-950 border border-gray-800 rounded-xl ${isFullscreen ? 'fixed inset-0 z-50 rounded-none border-0' : 'min-h-[500px]'} flex flex-col`}
      >
        {/* TV header */}
        <div className={`flex items-center justify-between ${isFullscreen ? 'px-12 py-6' : 'px-6 py-4'} border-b border-gray-800`}>
          <div className="flex items-center gap-3">
            <Trophy size={isFullscreen ? 32 : 20} className="text-amber-400" />
            <span className={`font-bold text-white ${isFullscreen ? 'text-3xl' : 'text-lg'}`}>
              {tvData?.agencyName || 'Agency Leaderboard'}
            </span>
          </div>
          <div className={`text-gray-400 font-mono tabular-nums ${isFullscreen ? 'text-2xl' : 'text-sm'}`}>
            {timeStr}
          </div>
        </div>

        {/* Panel content */}
        <div className={`flex-1 flex flex-col ${isFullscreen ? 'p-12' : 'p-6'}`}>
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Spinner size={40} />
            </div>
          ) : renderPanel()}
        </div>

        {/* Panel nav arrows in fullscreen */}
        {isFullscreen && (
          <>
            <button
              onClick={() => setPanelIdx(i => (i - 1 + TV_PANELS.length) % TV_PANELS.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-gray-800/80 hover:bg-gray-700 text-white"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => setPanelIdx(i => (i + 1) % TV_PANELS.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-gray-800/80 hover:bg-gray-700 text-white"
            >
              <ChevronRight size={24} />
            </button>
            <button
              onClick={toggleFullscreen}
              className="absolute top-4 right-4 p-2 rounded-full bg-gray-800/80 hover:bg-gray-700 text-white"
            >
              <Minimize size={18} />
            </button>
          </>
        )}

        {/* Bottom dots in fullscreen */}
        {isFullscreen && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
            {TV_PANELS.map((_, i) => (
              <button
                key={i}
                onClick={() => setPanelIdx(i)}
                className={`w-3 h-3 rounded-full transition-colors ${i === panelIdx ? 'bg-amber-400' : 'bg-gray-700'}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// AEP SEASON TAB
// ============================================================================
function AEPTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE}/agency-leaderboard/aep`, { credentials: 'include' })
      .then(r => r.json())
      .then(json => { if (json.ok) setData(json.data); })
      .catch(err => console.error('[aep]', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-24"><Spinner size={32} /></div>;
  if (!data) return <div className="py-24 text-center text-gray-500">Failed to load AEP data</div>;

  const maxEnrollments = Math.max(1, ...data.agents.map(a => a.enrollments));

  return (
    <div className="space-y-6">
      {/* AEP header */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Star className="text-amber-400" size={22} />
              AEP Season {data.year}
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              Oct 15 – Dec 7, {data.year}
            </p>
          </div>
          <div className="flex gap-4">
            {data.isActive && data.daysRemaining !== null && (
              <div className="text-center">
                <p className="text-4xl font-bold text-amber-400 tabular-nums">{data.daysRemaining}</p>
                <p className="text-gray-400 text-xs">days remaining</p>
              </div>
            )}
            {!data.isActive && data.daysUntilAep !== null && (
              <div className="text-center">
                <p className="text-4xl font-bold text-gray-300 tabular-nums">{data.daysUntilAep}</p>
                <p className="text-gray-400 text-xs">days until AEP</p>
              </div>
            )}
            <div className="text-center">
              <p className="text-4xl font-bold text-white tabular-nums">{data.totalAepEnrollments}</p>
              <p className="text-gray-400 text-xs">total enrollments</p>
            </div>
          </div>
        </div>
        {!data.isActive && (
          <div className={`mt-4 px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${
            data.daysUntilAep !== null
              ? 'bg-blue-900/30 border border-blue-800 text-blue-300'
              : 'bg-gray-800 border border-gray-700 text-gray-400'
          }`}>
            <Calendar size={13} />
            {data.daysUntilAep !== null
              ? `AEP starts in ${data.daysUntilAep} days — showing preview mode`
              : 'AEP Season has ended — showing historical data'}
          </div>
        )}
      </div>

      {/* Enrollment Race */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <BarChart2 size={16} className="text-amber-400" />
          Enrollment Race
        </h3>
        <div className="space-y-3">
          {data.agents.map((agent, i) => (
            <div key={agent.agentId} className="flex items-center gap-3">
              <div className="w-6 text-center">
                <RankBadge rank={i + 1} />
              </div>
              <div className="w-28 truncate text-sm text-gray-300">{agent.name}</div>
              <div className="flex-1 h-7 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full flex items-center pl-3 text-xs font-bold text-gray-950 transition-all duration-700 ${i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-700' : 'bg-gray-700'}`}
                  style={{ width: `${Math.max(4, (agent.enrollments / maxEnrollments) * 100)}%` }}
                >
                  {agent.enrollments > 0 && agent.enrollments}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {[...(agent.earnedBadges || []), ...(agent.liveBadges || [])].slice(0, 3).map(b => (
                  <span key={b.key} title={b.label} className="text-base">{b.emoji}</span>
                ))}
              </div>
            </div>
          ))}
          {data.agents.length === 0 && (
            <p className="text-gray-600 text-sm">No enrollments yet for AEP {data.year}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Carrier Competition */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Target size={16} className="text-amber-400" />
            Carrier Breakdown
          </h3>
          {data.carrierBreakdown.length === 0 ? (
            <p className="text-gray-600 text-sm">No carrier data yet</p>
          ) : (
            <div className="space-y-2">
              {data.carrierBreakdown.map(c => (
                <div key={c.carrier} className="flex items-center gap-2">
                  <div className="flex-1 text-sm text-gray-300 truncate">{c.carrier}</div>
                  <div className="w-32 h-3 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400/80 rounded-full"
                      style={{ width: `${(c.enrollments / (data.carrierBreakdown[0]?.enrollments || 1)) * 100}%` }}
                    />
                  </div>
                  <div className="text-amber-400 font-bold text-sm tabular-nums w-8 text-right">{c.enrollments}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AEP Badges */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Award size={16} className="text-amber-400" />
            AEP Badges
          </h3>
          <div className="space-y-2">
            {[
              { key: 'aep_starter', label: 'AEP Starter', emoji: '🚀', desc: 'First enrollment this AEP' },
              { key: 'century_club', label: 'Century Club', emoji: '💯', desc: '100+ enrollments' },
              { key: 'speed_demon', label: 'Speed Demon', emoji: '⚡', desc: '5+ enrollments in one day' },
              { key: 'completionist', label: 'Completionist', emoji: '✅', desc: '100% coverage reviews done' },
            ].map(badgeDef => {
              const earners = data.agents.filter(a =>
                (a.earnedBadges || []).some(b => b.key === badgeDef.key) ||
                (a.liveBadges || []).some(b => b.key === badgeDef.key)
              );
              return (
                <div key={badgeDef.key} className="flex items-center gap-3">
                  <span className="text-2xl">{badgeDef.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">{badgeDef.label}</p>
                    <p className="text-xs text-gray-500">{badgeDef.desc}</p>
                  </div>
                  <div className="text-xs text-gray-500 text-right flex-shrink-0">
                    {earners.length > 0
                      ? <span className="text-amber-400">{earners.map(e => e.name.split(' ')[0]).join(', ')}</span>
                      : <span className="text-gray-700">—</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CONTESTS TAB
// ============================================================================
function ContestsTab() {
  const [contests, setContests] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', metric: 'enrollments',
    targetValue: '', prizeDescription: '', startsAt: '', endsAt: '',
    participantMode: 'all', participants: [],
  });
  const [saving, setSaving] = useState(false);

  const fetchContests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/agency-leaderboard/contests`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setContests(json.data);
    } catch (err) {
      console.error('[contests]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContests(); }, [fetchContests]);

  const createContest = async () => {
    if (!form.name || !form.startsAt || !form.endsAt) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/agency-leaderboard/contests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (json.ok) {
        setShowCreate(false);
        setForm({ name: '', description: '', metric: 'enrollments', targetValue: '', prizeDescription: '', startsAt: '', endsAt: '', participantMode: 'all', participants: [] });
        fetchContests();
      }
    } catch (err) {
      console.error('[contest create]', err);
    } finally {
      setSaving(false);
    }
  };

  const cancelContest = async (id) => {
    try {
      await fetch(`${BASE}/agency-leaderboard/contests/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      fetchContests();
    } catch (err) {
      console.error('[contest cancel]', err);
    }
  };

  const active = contests.filter(c => c.status === 'active' && new Date(c.ends_at) > new Date());
  const history = contests.filter(c => c.status !== 'active' || new Date(c.ends_at) <= new Date());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-semibold text-lg">Contests & Challenges</h2>
          <p className="text-gray-500 text-sm">Motivational competitions — track progress and celebrate winners</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-4 py-2 bg-amber-400 text-gray-950 rounded-lg font-semibold text-sm hover:bg-amber-300 transition-colors"
        >
          <Plus size={15} /> New Contest
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-gray-900 border border-amber-400/30 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold">Create Contest</h3>
            <button onClick={() => setShowCreate(false)} className="text-gray-500 hover:text-white">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-400 block mb-1">Contest Name *</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Q2 Enrollment Sprint"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-amber-400 outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-400 block mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
                placeholder="Optional details about the contest..."
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-amber-400 outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Metric</label>
              <select
                value={form.metric}
                onChange={e => setForm(f => ({ ...f, metric: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-400 outline-none"
              >
                {METRICS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Target Value</label>
              <input
                type="number"
                value={form.targetValue}
                onChange={e => setForm(f => ({ ...f, targetValue: e.target.value }))}
                placeholder="e.g. 50"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-amber-400 outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Start Date *</label>
              <input
                type="date"
                value={form.startsAt}
                onChange={e => setForm(f => ({ ...f, startsAt: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-400 outline-none"
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">End Date *</label>
              <input
                type="date"
                value={form.endsAt}
                onChange={e => setForm(f => ({ ...f, endsAt: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-400 outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-gray-400 block mb-1">Prize / Reward Description</label>
              <input
                value={form.prizeDescription}
                onChange={e => setForm(f => ({ ...f, prizeDescription: e.target.value }))}
                placeholder="e.g. $500 gift card + team dinner"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-amber-400 outline-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button
              onClick={createContest}
              disabled={saving || !form.name || !form.startsAt || !form.endsAt}
              className="px-4 py-2 bg-amber-400 text-gray-950 rounded-lg font-semibold text-sm hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Creating...' : 'Create Contest'}
            </button>
          </div>
        </div>
      )}

      {/* Active contests */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : (
        <>
          <div>
            <h3 className="text-gray-400 text-xs uppercase tracking-wider mb-3">Active Contests ({active.length})</h3>
            {active.length === 0 ? (
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
                <Gift size={32} className="text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No active contests. Create one to motivate your team!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {active.map(contest => {
                  const maxScore = Math.max(1, ...(contest.progress || []).map(p => p.score));
                  const target = contest.targetValue || maxScore;
                  return (
                    <div key={contest.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div>
                          <h4 className="text-white font-semibold">{contest.name}</h4>
                          {contest.description && <p className="text-gray-500 text-sm mt-0.5">{contest.description}</p>}
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-xs bg-gray-800 border border-gray-700 rounded px-2 py-0.5 text-gray-400">
                              {METRICS.find(m => m.value === contest.metric)?.label || contest.metric}
                            </span>
                            {contest.prize_description && (
                              <span className="text-xs bg-amber-400/10 border border-amber-400/20 rounded px-2 py-0.5 text-amber-400">
                                🏆 {contest.prize_description}
                              </span>
                            )}
                            <span className="text-xs text-gray-600">
                              {contest.daysLeft}d remaining
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => cancelContest(contest.id)}
                          className="text-gray-600 hover:text-red-400 transition-colors p-1 flex-shrink-0"
                          title="Cancel contest"
                        >
                          <X size={16} />
                        </button>
                      </div>

                      {/* Leaderboard within contest */}
                      <div className="space-y-2">
                        {(contest.progress || []).slice(0, 6).map((p, i) => (
                          <div key={p.agentId} className="flex items-center gap-3">
                            <div className="w-5 text-center flex-shrink-0">
                              {i === 0 ? <Crown size={14} className="text-amber-400" /> : <span className="text-gray-600 text-xs">{i + 1}</span>}
                            </div>
                            <div className="w-28 truncate text-sm text-gray-300">{p.name}</div>
                            <div className="flex-1 h-4 bg-gray-800 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${i === 0 ? 'bg-amber-400' : 'bg-gray-600'}`}
                                style={{ width: `${Math.max(2, (p.score / target) * 100)}%` }}
                              />
                            </div>
                            <div className="text-sm font-bold tabular-nums text-white w-12 text-right flex-shrink-0">
                              {typeof p.score === 'number' && contest.metric === 'commission'
                                ? `$${p.score.toLocaleString()}`
                                : p.score}
                            </div>
                          </div>
                        ))}
                      </div>

                      {contest.leader && (
                        <p className="text-xs text-gray-500 mt-3">
                          Projected winner: <span className="text-amber-400 font-medium">{contest.leader.name}</span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* History */}
          {history.length > 0 && (
            <div>
              <h3 className="text-gray-400 text-xs uppercase tracking-wider mb-3">History</h3>
              <div className="space-y-2">
                {history.map(contest => (
                  <div key={contest.id} className="bg-gray-900/50 border border-gray-800 rounded-lg p-4 flex items-center justify-between gap-3 opacity-60">
                    <div>
                      <p className="text-gray-300 text-sm font-medium">{contest.name}</p>
                      <p className="text-gray-600 text-xs">{METRICS.find(m => m.value === contest.metric)?.label} · {contest.status}</p>
                    </div>
                    {contest.leader && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Winner</p>
                        <p className="text-amber-400 text-sm font-medium">{contest.leader.name}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function AgencyLeaderboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('rankings');
  const [userRole, setUserRole] = useState('agency_owner');

  // Fetch current user role for badge visibility
  useEffect(() => {
    fetch(`${BASE}/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(json => { if (json.user?.role) setUserRole(json.user.role); })
      .catch(() => {});
  }, []);

  // Tour
  const [tourStep, setTourStep] = useState(0);
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem('agency-leaderboard-v2-tour-dismissed')) setShowTour(true);
  }, []);
  const advanceTour = useCallback(() => {
    setTourStep(s => {
      if (s + 1 >= TOUR_STEPS.length) {
        localStorage.setItem('agency-leaderboard-v2-tour-dismissed', 'true');
        setShowTour(false);
        return 0;
      }
      return s + 1;
    });
  }, []);
  const backTour = useCallback(() => setTourStep(s => Math.max(0, s - 1)), []);
  const dismissTour = useCallback(() => {
    localStorage.setItem('agency-leaderboard-v2-tour-dismissed', 'true');
    setShowTour(false);
  }, []);
  useEffect(() => {
    if (!showTour) return;
    const h = e => {
      if (e.key === 'Escape') dismissTour();
      if (e.key === 'ArrowRight' || e.key === 'Enter') advanceTour();
      if (e.key === 'ArrowLeft') backTour();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [showTour, advanceTour, backTour, dismissTour]);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/agency/dashboard')}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Trophy size={20} className="text-amber-400" />
                Agency Leaderboard
              </h1>
              <p className="text-gray-500 text-xs">Performance rankings across your agency</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-900/60 border-b border-gray-800" data-tour="lb-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-0">
            {TABS.map(tab => {
              const now = new Date();
              const aepStart = new Date(now.getFullYear(), 9, 15);
              const aepEnd = new Date(now.getFullYear(), 11, 8);
              const isAepActive = now >= aepStart && now <= aepEnd;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors relative ${
                    activeTab === tab.id
                      ? 'border-amber-400 text-amber-400'
                      : 'border-transparent text-gray-500 hover:text-gray-300'
                  }`}
                >
                  <tab.icon size={14} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.id === 'aep' && isAepActive && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === 'rankings' && <RankingsTab userRole={userRole} />}
        {activeTab === 'tv' && <TVModeTab />}
        {activeTab === 'aep' && <AEPTab />}
        {activeTab === 'contests' && <ContestsTab />}
      </div>

      {/* Tour */}
      {showTour && (
        <TourBubble
          stepData={TOUR_STEPS[tourStep]}
          stepIndex={tourStep}
          totalSteps={TOUR_STEPS.length}
          onNext={advanceTour}
          onBack={backTour}
          onDismiss={dismissTour}
          splashIcon={<Trophy className="w-10 h-10 text-amber-400" />}
          maskId="agency-leaderboard-v2-tour-mask"
        />
      )}
    </div>
  );
}
